import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, AwardIcon, TrendingUpIcon, UsersIcon, StarIcon, ZapIcon, TargetIcon, HeartIcon } from 'lucide-react';
export function VipPartnerProgram() {
  const benefits = [{
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1923" />,
    title: 'Elevate Your Event Brand',
    description: 'Stand out from competitors by offering world-class VIP experiences that attendees will talk about for years.'
  }, {
    icon: <TrendingUpIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1924" />,
    title: 'Increase Sponsorship Value',
    description: 'Create premium sponsorship tiers with exclusive access to VIP experiences, commanding higher rates.'
  }, {
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1925" />,
    title: 'Retain Top Attendees',
    description: 'Give your most valuable attendees compelling reasons to return year after year with unforgettable experiences.'
  }, {
    icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1926" />,
    title: 'Zero Event Planning Burden',
    description: 'We handle all logistics, planning, and execution while you focus on your core conference activities.'
  }, {
    icon: <ZapIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1927" />,
    title: 'Turn-Key Implementation',
    description: 'From concept to execution, we deliver fully managed VIP experiences that integrate seamlessly with your event.'
  }, {
    icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1928" />,
    title: 'Strengthen Community Bonds',
    description: 'Create intimate networking opportunities for your top speakers, sponsors, and attendees in exclusive settings.'
  }];
  const experienceTypes = [{
    title: 'Pre-Conference VIP Retreat',
    description: 'Exclusive 2-3 day beach retreat before your main event for speakers, sponsors, and top attendees.',
    features: ['Private beachfront venue', 'Executive networking sessions', 'Luxury accommodations', 'Curated activities and experiences', 'Professional facilitation'],
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Post-Conference Wind-Down',
    description: 'Reward your VIPs with a relaxing post-event experience to decompress and deepen connections.',
    features: ['Wellness and recovery focus', 'Sunset yacht experiences', 'Spa and relaxation', 'Casual networking opportunities', 'Seamless transition from conference'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Mid-Conference VIP Day',
    description: 'Break up your multi-day conference with an exclusive day experience for top-tier attendees.',
    features: ['Day trips to unique locations', 'Team building activities', 'Private dining experiences', 'Cultural immersion', 'Photography and content creation'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  const howItWorks = [{
    step: '1',
    title: 'Discovery Call',
    description: 'We learn about your conference, attendee profile, and VIP experience goals.'
  }, {
    step: '2',
    title: 'Custom Proposal',
    description: 'Receive a tailored VIP experience package designed specifically for your event.'
  }, {
    step: '3',
    title: 'Seamless Integration',
    description: 'We coordinate with your team to integrate the VIP experience into your event schedule.'
  }, {
    step: '4',
    title: 'White-Glove Execution',
    description: 'Our team handles every detail while you receive regular updates and peace of mind.'
  }];
  const idealFor = ['Technology conferences with 500+ attendees', 'Industry trade shows and exhibitions', 'Professional association annual meetings', 'Executive summits and leadership forums', 'Innovation and startup conferences', 'International business conferences'];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1929">
      {/* Hero */}
      <section className="relative bg-[#0099A1] py-24 overflow-hidden" data-id="element-1930">
        <div className="absolute inset-0 opacity-10" data-id="element-1931">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1932">
            <defs data-id="element-1933">
              <pattern id="wave" patternUnits="userSpaceOnUse" width="100" height="20" patternTransform="rotate(45)" data-id="element-1934">
                <path d="M0 10 Q 12.5 0, 25 10 T 50 10 T 75 10 T 100 10" fill="none" stroke="white" strokeWidth="2" data-id="element-1935" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave)" data-id="element-1936" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1937">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1938">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6" data-id="element-1939">
              <TargetIcon className="h-4 w-4 mr-2" data-id="element-1940" />
              For Conference & Exhibition Organizers
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-1941">
              VIP Experience Partner Program
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8" data-id="element-1942">
              Transform Your Conference with Exclusive Beach Experiences That
              Your Most Valuable Attendees Will Never Forget
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1943">
              <Button variant="primary" size="lg" to="/contact" data-id="element-1944">
                Schedule Discovery Call
              </Button>
              <Button variant="outline" size="lg" className="bg-white/10 border-white text-white hover:bg-white/20" data-id="element-1945">
                Download Program Overview
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Problem/Solution */}
      <section className="py-16 bg-white" data-id="element-1946">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1947">
          <div className="max-w-4xl mx-auto" data-id="element-1948">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-1949">
              <div data-id="element-1950">
                <h2 className="text-3xl font-bold text-[#374047] mb-6" data-id="element-1951">
                  The Challenge Conference Organizers Face
                </h2>
                <div className="space-y-4" data-id="element-1952">
                  <div className="flex items-start" data-id="element-1953">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-1954"></div>
                    <p className="text-[#374047]/80" data-id="element-1955">
                      Sponsors demanding more value and unique activation
                      opportunities
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1956">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-1957"></div>
                    <p className="text-[#374047]/80" data-id="element-1958">
                      Top speakers and attendees expecting premium experiences
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1959">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-1960"></div>
                    <p className="text-[#374047]/80" data-id="element-1961">
                      Difficulty differentiating your event from competitors
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1962">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-1963"></div>
                    <p className="text-[#374047]/80" data-id="element-1964">
                      Limited time and resources to plan additional VIP
                      experiences
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-1965">
                <h2 className="text-3xl font-bold text-[#374047] mb-6" data-id="element-1966">
                  The DestaMeet Solution
                </h2>
                <div className="space-y-4" data-id="element-1967">
                  <div className="flex items-start" data-id="element-1968">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-1969" />
                    <p className="text-[#374047]/80" data-id="element-1970">
                      Turn-key VIP experiences that require zero effort from
                      your team
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1971">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-1972" />
                    <p className="text-[#374047]/80" data-id="element-1973">
                      Premium add-ons that increase event revenue and
                      sponsorship value
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1974">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-1975" />
                    <p className="text-[#374047]/80" data-id="element-1976">
                      Unforgettable experiences that boost attendee retention
                      and word-of-mouth
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-1977">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-1978" />
                    <p className="text-[#374047]/80" data-id="element-1979">
                      Professional execution by destination management experts
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1980">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1981">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1982">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1983">
              Why Partner with DestaMeet?
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1984">
              We handle everything so you can focus on delivering an exceptional
              core conference experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1985">
            {benefits.map((benefit, index) => <Card key={index} className="h-full" data-id="element-1986">
                <CardBody className="p-6" data-id="element-1987">
                  <div className="mb-4" data-id="element-1988">{benefit.icon}</div>
                  <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-1989">
                    {benefit.title}
                  </h3>
                  <p className="text-[#374047]/80" data-id="element-1990">{benefit.description}</p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>
      {/* Experience Types */}
      <section className="py-16 bg-white" data-id="element-1991">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1992">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1993">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1994">
              VIP Experience Options
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1995">
              Flexible formats designed to complement your conference schedule
              and objectives.
            </p>
          </div>
          <div className="space-y-16" data-id="element-1996">
            {experienceTypes.map((experience, index) => <div key={index} className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-1997">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-1998">
                  <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-1999">
                    {experience.title}
                  </h3>
                  <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2000">
                    {experience.description}
                  </p>
                  <ul className="space-y-3" data-id="element-2001">
                    {experience.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-2002">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2003" />
                        <span className="ml-3 text-[#374047]" data-id="element-2004">{feature}</span>
                      </li>)}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-2005">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-2006">
                    <img src={experience.image} alt={experience.title} className="w-full h-full object-cover" data-id="element-2007" />
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2008">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2009">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2010">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2011">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2012">
              From first conversation to flawless execution, we make it easy.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto" data-id="element-2013">
            {howItWorks.map((item, index) => <div key={index} className="text-center" data-id="element-2014">
                <div className="w-16 h-16 bg-[#0099A1] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-2015">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-2016">
                  {item.title}
                </h3>
                <p className="text-[#374047]/80" data-id="element-2017">{item.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Ideal For */}
      <section className="py-16 bg-white" data-id="element-2018">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2019">
          <div className="max-w-4xl mx-auto" data-id="element-2020">
            <h2 className="text-3xl font-bold text-[#374047] mb-8 text-center" data-id="element-2021">
              Ideal For
            </h2>
            <div className="grid md:grid-cols-2 gap-4" data-id="element-2022">
              {idealFor.map((item, index) => <div key={index} className="flex items-center p-4 bg-[#F4E8D0]/30 rounded-lg" data-id="element-2023">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-2024" />
                  <span className="text-[#374047]" data-id="element-2025">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2026">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2027">
          <div className="max-w-4xl mx-auto" data-id="element-2028">
            <h2 className="text-3xl font-bold text-[#374047] mb-12 text-center" data-id="element-2029">
              What Conference Organizers Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-2030">
              <Card data-id="element-2031">
                <CardBody className="p-6" data-id="element-2032">
                  <div className="flex mb-4" data-id="element-2033">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-[#FFB800] fill-current" data-id="element-2034" />)}
                  </div>
                  <p className="text-[#374047]/80 mb-4" data-id="element-2035">
                    "The VIP beach experience we added through DestaMeet became
                    the most talked-about part of our conference. Our sponsors
                    were thrilled with the exclusive access, and attendee
                    satisfaction scores went through the roof."
                  </p>
                  <div className="flex items-center" data-id="element-2036">
                    <div data-id="element-2037">
                      <p className="font-semibold text-[#374047]" data-id="element-2038">
                        Sarah Mitchell
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2039">
                        Director, TechSummit Global
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card data-id="element-2040">
                <CardBody className="p-6" data-id="element-2041">
                  <div className="flex mb-4" data-id="element-2042">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-[#FFB800] fill-current" data-id="element-2043" />)}
                  </div>
                  <p className="text-[#374047]/80 mb-4" data-id="element-2044">
                    "Working with DestaMeet was effortless. They handled
                    everything from logistics to execution, allowing us to focus
                    on our core conference. The post-event feedback from our VIP
                    attendees was overwhelmingly positive."
                  </p>
                  <div className="flex items-center" data-id="element-2045">
                    <div data-id="element-2046">
                      <p className="font-semibold text-[#374047]" data-id="element-2047">
                        Michael Chen
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2048">
                        VP Events, Innovation Forum
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 bg-[#0099A1] relative overflow-hidden" data-id="element-2049">
        <div className="absolute inset-0 opacity-10" data-id="element-2050">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-2051">
            <defs data-id="element-2052">
              <pattern id="wave-cta" patternUnits="userSpaceOnUse" width="100" height="20" patternTransform="rotate(45)" data-id="element-2053">
                <path d="M0 10 Q 12.5 0, 25 10 T 50 10 T 75 10 T 100 10" fill="none" stroke="white" strokeWidth="2" data-id="element-2054" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-cta)" data-id="element-2055" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2056">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2057">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2058">
              Ready to Elevate Your Conference?
            </h2>
            <p className="text-xl text-white/80 mb-8" data-id="element-2059">
              Let's discuss how we can create an unforgettable VIP experience
              for your most valuable attendees.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2060">
              <Button variant="primary" size="lg" to="/contact" data-id="element-2061">
                Schedule Discovery Call
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" href="https://wa.me/14154170322" data-id="element-2062">
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}